import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { getRedirectResult, onIdTokenChanged } from "firebase/auth";
import { auth } from "../firebase";
import {
  authAPI,
  billingAPI,
  invitesAPI,
  type CompanySummary,
  type InviteAcceptResponse,
} from "../services/api";
import { useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { TriangleAlert } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useDataStore } from "@/store/useDataStore";
import { requireEmailVerification } from "@/config/auth";
import { useQueryClient } from "@tanstack/react-query";
import {
  captureAuthCompleted,
  identifyPosthogUser,
  resetPosthog,
} from "@/lib/posthog";
import { trackMetaPixelLeadOnce } from "@/lib/metaPixel";
import type { AxiosError } from "axios";
import {
  consumeTryReturn,
  hasTryLoginReturn,
  markKnownAccount,
  TRY_PATH,
} from "@/lib/tryLink";

export interface AuthContextType {
  user: {
    onboard_step: number;
    email: string;
    expired_at: string;
    tier: string;
    name: string;
    id: string;
    requires_plan_selection?: boolean;
    brands: any[];
    company: CompanySummary;
    companies?: CompanySummary[];
    picture: string;
    isWhitelisted?: boolean;
  } | null;
  emailVerified: boolean | null;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadingStep: boolean;
  setLoadingStep: React.Dispatch<React.SetStateAction<boolean>>;
  refreshUser: () => Promise<void>;
  markOnboardingPromptsSubmitted: () => void;
  setEmailVerifiedState: (value: boolean | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  emailVerified: null,
  loading: true,
  loadingStep: false,
  setLoadingStep: () => {},
  setLoading: () => {},
  refreshUser: async () => {},
  markOnboardingPromptsSubmitted: () => {},
  setEmailVerifiedState: () => {},
});

export const useAuth = () => useContext(AuthContext);

const SHELL_STATUS_STALE_MS = 60_000;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingStep, setLoadingStep] = useState(false);
  const processedAuthRef = useRef<string>("");
  const processingAuthRef = useRef<string>("");
  const acceptingInviteRef = useRef<{
    token: string;
    promise: Promise<InviteAcceptResponse>;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setStoredLimitPrompts } = useDataStore();

  const toAuthErrorMessage = (error: unknown) => {
    const axiosError = error as AxiosError<{ message?: string }>;
    const apiMessage = axiosError?.response?.data?.message;
    if (apiMessage) return apiMessage;

    const message = String(
      (error as { message?: string })?.message || "",
    ).trim();
    if (!message) return "Couldn't login!";

    return message
      .replace(/^Firebase:\s*/i, "")
      .replace(/\s*\(auth\/[^\)]+\)\.?/i, "")
      .trim();
  };

  const redirect = async (
    user: any,
    brand: string | null,
    isVerified: boolean,
  ) => {
    const verified = requireEmailVerification ? isVerified : true;
    const verifyPath = "/register/1?verify=pending";
    const verifyQuery = new URLSearchParams(location.search).get("verify");
    const isVerifyTokenRoute = /^\/verify\/[^/]+$/.test(location.pathname);
    const isRegisterPendingState =
      location.pathname === "/register/1" &&
      (verifyQuery === "pending" || verifyQuery === "invalid");
    const shouldReturnToTry =
      new URLSearchParams(window.location.search).get("returnTo") ===
        TRY_PATH || hasTryLoginReturn();
    if (!verified) {
      if (!isVerifyTokenRoute && !isRegisterPendingState) {
        navigate(verifyPath, { replace: true });
      }
      return;
    }
    if (user?.isWhitelisted === false && user?.onboard_step < 3) {
      if (location.pathname !== "/coming-soon") {
        navigate("/coming-soon", { replace: true });
      }
      return;
    }

    if (user?.onboard_step >= 3 && shouldReturnToTry) {
      consumeTryReturn();
      navigate("/try", { replace: true });
      return;
    }

    if (user?.onboard_step >= 3 && user?.requires_plan_selection) {
      let canEnterApp = false;
      try {
        const subscription = await queryClient.fetchQuery({
          queryKey: ["billing-subscription", user.id],
          queryFn: billingAPI.getSubscription,
          staleTime: SHELL_STATUS_STALE_MS,
        });
        canEnterApp = Boolean(subscription?.can_enter_app);
      } catch {
        canEnterApp = false;
      }
      if (!canEnterApp) {
        if (location.pathname !== "/onboarding/plan") {
          navigate("/onboarding/plan", { replace: true });
        }
        return;
      }
      trackMetaPixelLeadOnce(user?.id);
    }

    if (user.onboard_step >= 3) {
      const isLogin = location.pathname === "/login";
      const isRegisterStep = /^\/register\/[1-4]$/.test(location.pathname);
      const isSignup = location.pathname === "/signup";
      const isVerifyEmail =
        location.pathname === "/verify-email" ||
        /^\/verify\/[^/]+$/.test(location.pathname);
      const isPlanFlow =
        location.pathname === "/onboarding/plan" ||
        location.pathname === "/billing/success";
      if (
        isLogin ||
        isRegisterStep ||
        isSignup ||
        isVerifyEmail ||
        isPlanFlow
      ) {
        if (consumeTryReturn()) {
          navigate("/try", { replace: true });
          return;
        }
        const shouldRedirectToStrategy =
          typeof window !== "undefined" &&
          window.sessionStorage.getItem("nobori_redirect_to_strategy_once") ===
            "1";
        if (shouldRedirectToStrategy) {
          window.sessionStorage.setItem(
            "nobori_onboarding_strategy_entry_once",
            "1",
          );
          window.sessionStorage.removeItem("nobori_redirect_to_strategy_once");
          navigate("/dashboard/get-cited/strategy", { replace: true });
          return;
        }
        navigate(
          `/dashboard/overview${
            brand ? `?brandId=${encodeURIComponent(brand)}` : ""
          }`,
          { replace: true },
        );
      }
      return;
    }

    navigate(`/register/2${brand ? `?brand=${brand}` : ""}`, {
      replace: true,
    });
  };

  async function getUser(fireBaseUser: any) {
    let apiUser;
    const resolveIdToken = async (forceRefresh = false) => {
      const token = await fireBaseUser?.getIdToken(forceRefresh);
      return String(token || "").trim();
    };
    const wait = (ms: number) =>
      new Promise<void>((resolve) => window.setTimeout(resolve, ms));

    try {
      apiUser = await authAPI.getMe();
      return apiUser;
    } catch {
      let lastLoginError: unknown = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const forceRefresh = attempt > 0;
        const token = await resolveIdToken(forceRefresh);
        if (!token) {
          lastLoginError = new Error("Failed to get Firebase ID token");
          await wait(200 * (attempt + 1));
          continue;
        }
        try {
          apiUser = await authAPI.login({ idToken: token });
          break;
        } catch (loginError) {
          lastLoginError = loginError;
          await wait(250 * (attempt + 1));
        }
      }

      if (!apiUser) {
        throw lastLoginError || new Error("Failed to login after redirect");
      }

      apiUser.user = {
        ...apiUser.user,
        email: fireBaseUser?.email || "",
        name: fireBaseUser?.displayName || "",
      };
      setStoredLimitPrompts(null);
      if ((window as any).$crisp) {
        (window as any).$crisp.push(["do", "session:reset"]);
      }
      return apiUser;
    }
  }

  const acceptInviteOnce = (token: string) => {
    if (acceptingInviteRef.current?.token === token) {
      return acceptingInviteRef.current.promise;
    }
    const promise = invitesAPI.acceptInvite(token);
    acceptingInviteRef.current = { token, promise };
    return promise;
  };

  const getUserInfo = async (
    fireBaseUser: any,
    brand: string | null,
  ): Promise<boolean> => {
    try {
      let apiUser = await getUser(fireBaseUser);
      const verified = requireEmailVerification
        ? Boolean(fireBaseUser?.emailVerified)
        : true;
      const inviteToken = window.localStorage.getItem("invite_token");
      const hasPrefilledBrandDomain = Boolean(brand);
      const signupSource = inviteToken
        ? "invite"
        : hasPrefilledBrandDomain
          ? "marketing_home"
          : "direct";
      const authMethod = fireBaseUser?.providerData?.some(
        (provider: any) => String(provider?.providerId || "") === "google.com",
      )
        ? "google"
        : "email";
      let landingBrand = brand;
      if (verified && inviteToken) {
        try {
          const accepted = await acceptInviteOnce(inviteToken);
          window.localStorage.removeItem("invite_token");
          landingBrand = accepted?.landing?.brandId || landingBrand;
          const userId = String(apiUser.user?.id || "").trim();
          if (userId && accepted?.landing?.companyId) {
            window.localStorage.setItem(
              `nobori_last_company_id:${userId}`,
              accepted.landing.companyId,
            );
          }
          if (userId && accepted?.landing?.brandId) {
            window.localStorage.setItem(
              `nobori_last_brand_id:${userId}`,
              accepted.landing.brandId,
            );
          }
          apiUser = await authAPI.getMe();
        } catch (error) {
          if ((error as AxiosError)?.response?.status === 400) {
            window.localStorage.removeItem("invite_token");
          }
          toast.error(toAuthErrorMessage(error), {
            icon: (
              <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
                <TriangleAlert className="w-4 h-4 text-black" />
              </div>
            ),
            position: "bottom-right",
          });
        } finally {
          acceptingInviteRef.current = null;
        }
      }
      const resolvedUser = {
        ...apiUser.user,
        email: apiUser.user?.email || fireBaseUser?.email || "",
        brands: apiUser.brands || [],
        company: apiUser.company || {},
        companies: apiUser.companies || [],
      };
      markKnownAccount();
      const isNewUser =
        Number(resolvedUser?.onboard_step || 0) <= 1 &&
        (!Array.isArray(resolvedUser?.brands) ||
          resolvedUser.brands.length === 0);
      setUser(resolvedUser);
      const authFingerprint = `${resolvedUser?.id || "unknown"}:${String(
        fireBaseUser?.metadata?.lastSignInTime || "",
      )}`;
      const authStorageKey = `nobori_auth_completed_v1:${authFingerprint}`;
      if (window.sessionStorage.getItem(authStorageKey) !== "1") {
        captureAuthCompleted({
          authMethod,
          isNewUser,
          signupSource,
        });
        window.sessionStorage.setItem(authStorageKey, "1");
      }
      identifyPosthogUser({
        user: resolvedUser,
        signupSource,
        authMethod,
      });
      await redirect(
        resolvedUser,
        landingBrand,
        Boolean(fireBaseUser?.emailVerified),
      );
      return true;
    } catch (error) {
      console.error("Auth bootstrap failed", error);
      toast.error(toAuthErrorMessage(error), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      return false;
    } finally {
      setLoading(false);
      setLoadingStep(false);
    }
  };

  const refreshUser = async () => {
    const fireBaseUser = auth.currentUser;
    if (!fireBaseUser) return;
    setEmailVerified(
      requireEmailVerification ? Boolean(fireBaseUser.emailVerified) : true,
    );
    const apiUser = await getUser(fireBaseUser);
    const resolvedUser = {
      ...apiUser.user,
      email: apiUser.user?.email || fireBaseUser?.email || "",
      brands: apiUser.brands || [],
      company: apiUser.company || {},
      companies: apiUser.companies || [],
    };
    setUser(resolvedUser);
  };

  const markOnboardingPromptsSubmitted = () => {
    setUser((current: any) => {
      if (!current) return current;
      return {
        ...current,
        onboard_step: Math.max(3, Number(current.onboard_step || 0)),
        requires_plan_selection: current.requires_plan_selection ?? true,
      };
    });
  };

  useEffect(() => {
    const inviteToken = new URLSearchParams(location.search).get("invite");
    if (inviteToken) {
      window.localStorage.setItem("invite_token", inviteToken);
    }
  }, [location.search]);

  useEffect(() => {
    const processFirebaseUser = async (fireBaseUser: any) => {
      const verificationState = requireEmailVerification
        ? fireBaseUser?.emailVerified
          ? "verified"
          : "unverified"
        : "verification_off";
      const fingerprint = `${fireBaseUser?.uid || "unknown"}:${String(
        fireBaseUser?.metadata?.lastSignInTime || "",
      )}:${verificationState}`;
      if (
        processedAuthRef.current === fingerprint ||
        processingAuthRef.current === fingerprint
      ) {
        return;
      }

      processingAuthRef.current = fingerprint;
      try {
        setEmailVerified(
          requireEmailVerification ? Boolean(fireBaseUser.emailVerified) : true,
        );
        const params = new URLSearchParams(window.location.search);
        const brand = params.get("brand");
        const ok = await getUserInfo(fireBaseUser, brand);
        if (ok) {
          processedAuthRef.current = fingerprint;
        }
      } finally {
        if (processingAuthRef.current === fingerprint) {
          processingAuthRef.current = "";
        }
      }
    };

    const consumeRedirectResult = async () => {
      try {
        const redirectResult = await getRedirectResult(auth);
        if (redirectResult?.user) {
          await processFirebaseUser(redirectResult.user);
        }
      } catch (error) {
        console.error("getRedirectResult failed", error);
        toast.error(toAuthErrorMessage(error), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
      }
    };

    void consumeRedirectResult();

    const unsubscribe = onIdTokenChanged(auth, async (fireBaseUser) => {
      if (fireBaseUser) {
        await processFirebaseUser(fireBaseUser);
      } else {
        processedAuthRef.current = "";
        processingAuthRef.current = "";
        setUser(null);
        setEmailVerified(null);
        resetPosthog();
        setLoading(false);
        setLoadingStep(false);
      }
    });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        emailVerified,
        loading,
        setLoading,
        loadingStep,
        setLoadingStep,
        refreshUser,
        markOnboardingPromptsSubmitted,
        setEmailVerifiedState: setEmailVerified,
      }}
    >
      <Toaster></Toaster>
      {children}
    </AuthContext.Provider>
  );
};
